import { env } from "utils/env";

/**
 * The root URL for the request API, set in ../../.env
 *
 * @return {string} The string set by the env var `VITE_API_ROOT`
 */
const API_ROOT = env.VITE_API_ROOT;

/**
 * Checks if a network request came back fine, and throws an error if not.
 *
 * The body is read as TEXT and parsed here rather than handed to
 * `response.json()`, because a Response body is a single-use stream: once
 * `json()` has rejected there is no second chance to recover the status. That
 * mattered — rails answers a good number of endpoints with `head :ok` /
 * `head :accepted` / `head :not_found` / `head :gone`, i.e. a 0-byte body
 * (often `content-type: text/html`), and some error paths return an HTML page.
 * Attaching `statusCode` inside a `.then()` off `json()` meant every one of
 * those rejected with a bare SyntaxError carrying no status at all, so the
 * downloads page could not tell 404 from 410 from a network blip and rendered
 * the generic error card for all three.
 *
 * A non-2xx therefore ALWAYS rejects with a usable `statusCode` — the only
 * signal callers have left when there is no JSON body to read.
 *
 * @param  {object}       response A response from a network request
 *
 * @return {object|Error}          The parsed JSON from the request
 */
const parseJSON = async (response) => {
  const ok = response.status >= 200 && response.status < 300;
  const rejectWithStatus = () => Promise.reject({ statusCode: response.status });

  let text;
  try {
    text = await response.text();
  } catch (err) {
    // The body stream failed mid-read. A non-2xx still carries a usable status;
    // a 2xx whose body can't be read is a genuine failure and must stay loud.
    if (ok) throw err;
    return rejectWithStatus();
  }

  // An empty body is not JSON. Treat it as "no content" — the rule the 204 case
  // has always used — so a `head :ok` / `head :accepted` succeeds rather than
  // dying in the parser.
  if (!text) return ok ? {} : rejectWithStatus();

  let body;
  try {
    body = JSON.parse(text);
  } catch (err) {
    // On an error response the status is what the caller needs, so swallow the
    // parse failure and surface the code. On a SUCCESS response there is
    // nothing to fall back on — a 2xx whose body isn't the JSON we asked for is
    // a real failure, and smoothing it into an empty object would hide it.
    if (ok) throw err;
    return rejectWithStatus();
  }

  return ok ? body : Promise.reject({ ...body, statusCode: response.status });
};

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} uri       The URI we want to request, appended to API_ROOT
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
const REQUEST_TIMEOUT_MS = 30000;

const request = (uri, options) => {
  // Defensively strip leading "api/" or "/api/" if present to prevent duplicate path segments
  const sanitizedPath = uri.replace(/^(?:\/?api\/)/, '');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  return fetch(`${API_ROOT}/${sanitizedPath}`, { ...options, signal: controller.signal })
    .then(response => {
      clearTimeout(timeoutId);
      if (response.status === 401 && options?.headers?.Authorization) {
        localStorage.removeItem('authToken');
        window.location.href = '/sign-in';
        return Promise.reject({ error: 'Session expired. Please sign in again.' });
      }
      return response;
    })
    .then(parseJSON)
    .catch(err => {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        return Promise.reject({ error: 'Request timed out. Please check your connection and try again.' });
      }
      throw err;
    });
};

export default request;
